import React, { useEffect, useState } from 'react';

import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { makeClassNames } from '../../../utils/styles';
import IconTennisRacket from '../../atoms/icons/icon-tennis-racket';
import { HorizontalNav } from '../../molecules';
import { CourtesyMessage } from '../../organisms';
import './tennis-results-categories.scss';
import { isClient } from '../../../utils/device-utils';

import { Category, getInitialCategories, getStyle } from './tennis-results-categories-helpers';

const classNames = () =>
  makeClassNames({
    root: 'tennis-results-categories',
    topNav: 'tennis-results-categories__top-nav',
    topItem: 'tennis-results-categories__top-item',
    bottomNav: 'tennis-results-categories__bottom-nav',
    bottomItem: 'tennis-results-categories__bottom-item',
    adv: 'tennis-results-categories__adv',
  });

export interface TennisResultsCategoriesProps extends IComponent {
  categories: Category[];
  onCategoryChange?: (category: Category) => void;
  targetSelector: string;
  adv?: {
    link: string;
    image: string;
  };
}

const localStorageKey = isClient() ? `${window.location.pathname}_activeIndex` : 'activeIndex';

const getInitialIndex = () => {
  if (!isClient()) {
    return 0;
  }
  return parseInt(window?.localStorage?.getItem(localStorageKey) || '0', 10) as number;
};

export function TennisResultsCategories({
  onCategoryChange,
  categories,
  targetSelector,
  adv,
}: TennisResultsCategoriesProps) {
  const [categoryList, setCategoryList] = useState(getInitialCategories(categories));
  const [activeIndex, setActiveIndex] = useState(getInitialIndex);
  const [style, setStyle] = useState(getStyle(targetSelector, categoryList[activeIndex]));

  const hasNoDates = categoryList.map((category) => category.dates?.length === 0).every(Boolean);

  const cn = classNames();
  const selectedCategoryDetails = categoryList[activeIndex];

  const updateCategoryActiveDate = (categoryName: string, selectedDate: string) => {
    setCategoryList((prevCategoryList) =>
      prevCategoryList.map((category) =>
        category.name === categoryName ? { ...category, activeDate: selectedDate } : category
      )
    );
  };

  const handleCategorySelection = (categoryName: string) => {
    setActiveIndex(categoryList.findIndex((category) => category.name === categoryName));
  };

  const handleDateSelection = (selectedDate: string) => {
    if (selectedCategoryDetails) {
      updateCategoryActiveDate(selectedCategoryDetails.name, selectedDate);
    }
  };

  useEffect(() => {
    setStyle(getStyle(targetSelector, selectedCategoryDetails));
    onCategoryChange?.(selectedCategoryDetails);
  }, [selectedCategoryDetails, onCategoryChange, targetSelector]);

  useEffect(() => {
    if (isClient()) {
      window.localStorage.setItem(localStorageKey, `${activeIndex}`);
    }
  }, [activeIndex]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: style }} />

      <div className={cn.root} data-empty={hasNoDates}>
        {hasNoDates ? (
          <CourtesyMessage
            text={t('tennis.results.courtesymsg')}
            backgroundIcon={<IconTennisRacket />}
            fontSize="18--22"
          />
        ) : (
          <>
            <HorizontalNav activeIndex={activeIndex} className={cn.topNav} role="tablist" aria-label="Categorie" isTab>
              {categoryList.map((category, index) => (
                <HorizontalNav.Item
                  key={category.label}
                  index={index}
                  onClick={() => handleCategorySelection(category.name)}
                  className={cn.topItem}
                  role="tab"
                >
                  {category.label}
                </HorizontalNav.Item>
              ))}
            </HorizontalNav>

            {selectedCategoryDetails && (
              <HorizontalNav
                key={selectedCategoryDetails.name}
                activeIndex={selectedCategoryDetails.dates!.indexOf(selectedCategoryDetails.activeDate)}
                className={cn.bottomNav}
                role="tablist"
                aria-label="Date"
                isTab
              >
                {selectedCategoryDetails.dates!.map((date, index) => (
                  <HorizontalNav.Item
                    key={date}
                    index={index}
                    onClick={() => handleDateSelection(date)}
                    className={cn.bottomItem}
                    role="tab"
                  >
                    <FormattedDate>{date}</FormattedDate>
                  </HorizontalNav.Item>
                ))}
              </HorizontalNav>
            )}

            {adv && <Adv {...adv} />}
          </>
        )}
      </div>
    </>
  );
}

function FormattedDate({ children }: { children: string }) {
  const parsedDate = new Date(children);
  const dayOfMonth = parsedDate.getDate();
  const monthAbbreviation = parsedDate.toLocaleString('it-IT', { month: 'short' }).toUpperCase();

  return (
    <>
      {dayOfMonth}
      <br />
      {monthAbbreviation}
    </>
  );
}

function Adv({ link, image }: { link: string; image: string }) {
  const cn = classNames();
  return (
    <div className={cn.adv}>
      <span>{t('sponsored-by')}</span>
      <a href={link} title="Immagine ADV">
        <img src={image} alt="" />
      </a>
    </div>
  );
}
